"use client";

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './MediaCard.module.css';
import { Publication } from '@/app/interfaces/publication';

interface MediaCardProps {
    item: Publication;
    type: 'podcast' | 'magazine' | 'event';
    variant?: 'default' | 'home';
}

export function MediaCard({ item, type, variant = 'default' }: MediaCardProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isModalOpen]);

    const wrapperClass = type === 'magazine'
        ? (variant === 'home' ? styles.homeMagazineCardWrapper : styles.magazineCardWrapper)
        : styles.scrollCardWrapper;
    const imageWrapperClass = type === 'magazine' ? styles.imageWrapperMagazine : styles.imageWrapperPodcast;

    const cardContent = (
        <div className={styles.card}>
            <div className={imageWrapperClass}>
                <Image
                    src={item.imageLink}
                    alt={item.title}
                    className={styles.galleryImage}
                    width={type === 'magazine' ? 600 : 800}
                    height={type === 'magazine' ? 840 : 450}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />

                {type === 'podcast' && (
                    <div className={styles.youtubePill}>
                        <svg viewBox="0 0 24 24" className={styles.youtubeIcon}>
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000" />
                        </svg>
                        <span>YouTube</span>
                    </div>
                )}
            </div>
            <p className={styles.caption} title={item.title}>{item.title}</p>
        </div>
    );

    if (type === 'magazine') {
        return (
            <>
                <motion.button
                    onClick={() => setIsModalOpen(true)}
                    className={`${wrapperClass} ${styles.magazineButton}`}
                    aria-label={`Open ${item.title}`}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                    {cardContent}
                </motion.button>

                {isMounted && createPortal(
                    <AnimatePresence>
                        {isModalOpen && item.link && (
                            <motion.div
                                className={styles.modalOverlay}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsModalOpen(false)}
                            >
                                <motion.div
                                    className={styles.modalContainer}
                                    onClick={(e) => e.stopPropagation()}
                                    initial={{ scale: 0.9, opacity: 0, y: 40 }}
                                    animate={{ scale: 1, opacity: 1, y: 0 }}
                                    exit={{ scale: 0.9, opacity: 0, y: 40 }}
                                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                >
                                    <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                    </button>
                                    <iframe
                                        allowFullScreen={true}
                                        scrolling="no"
                                        className={styles.heyzineIframe}
                                        src={item.link}
                                    />
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>,
                    document.body
                )}
            </>
        );
    }

    const destination = type === 'podcast' ? item.link : '/media';
    return (
        <Link href={destination} target='_blank' className={wrapperClass}>
            {cardContent}
        </Link>
    );
}